import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

export interface NavItem {
  id: string;
  route: string;
  icon: string;
  labelKey: string;
}

/** Scroll distance (px) in one direction before the bar reacts, so small jitters do nothing. */
const DIRECTION_THRESHOLD = 10;
/** Within this distance of either end the bar keeps its state: rubber-band bounce reverses scrollTop there. */
const EDGE_ZONE = 64;

@Component({
  selector: 'app-bottom-nav',
  templateUrl: './bottom-nav.component.html',
  styleUrls: ['./bottom-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  // The svg-icon markup is injected at runtime, so its paths can only be recoloured without encapsulation.
  encapsulation: ViewEncapsulation.None,
  imports: [MatMenuModule, RouterLink, RouterLinkActive, TranslatePipe, AngularSvgIconModule],
})
export class BottomNavComponent implements OnInit {
  @Input() items: NavItem[] = [];
  @Input() canAccessGeofence = false;
  @Output() logout = new EventEmitter<void>();

  /** True while the bar is slid out of view (scrolling down). */
  readonly hidden = signal(false);

  private scroller: HTMLElement | null = null;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  ngOnInit(): void {
    // The nav outlives the page: a new page (possibly too short to scroll, so it would never send a scroll event)
    // always starts with the nav showing.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.reveal());

    const scroller = (this.scroller = this.host.nativeElement.closest<HTMLElement>('.mat-drawer-content'));
    if (!scroller) return;
    this.destroyRef.onDestroy(() => scroller.classList.remove('nav-hidden'));

    // The page can scroll in more than one box (the drawer content, or a table's own scroller), and scroll events do
    // not bubble. Listening in the capture phase on the drawer content sees them all; event.target is the scroller.
    const lastTops = new WeakMap<EventTarget, number>();
    let lastTarget: EventTarget | null = null;
    let travelled = 0; // signed distance since the last direction change

    const onScroll = (event: Event) => {
      const el = event.target instanceof HTMLElement ? event.target : scroller;
      const max = el.scrollHeight - el.clientHeight;
      if (max <= EDGE_ZONE * 2) return; // too short to scroll meaningfully (and never worth hiding the nav for)

      // iOS overscroll reports values outside [0, max]; clamp so the bounce produces no delta.
      const top = Math.min(Math.max(el.scrollTop, 0), max);
      const previous = lastTops.get(el) ?? top;
      lastTops.set(el, top);
      if (lastTarget !== el) {
        lastTarget = el;
        travelled = 0;
      }
      const delta = top - previous;
      if (delta === 0) return;

      if (top <= EDGE_ZONE) {
        this.setHidden(false); // always visible at the top
        travelled = 0;
        return;
      }
      // Bottom edge: the bounce-back must not count as "scrolling up". Keep whatever state we had.
      if (max - top <= EDGE_ZONE) {
        travelled = 0;
        return;
      }

      // Reset the accumulator when the direction flips, then act once the threshold is passed.
      travelled = Math.sign(travelled) === Math.sign(delta) ? travelled + delta : delta;
      if (travelled > DIRECTION_THRESHOLD) this.setHidden(true);
      else if (travelled < -DIRECTION_THRESHOLD) this.setHidden(false);
    };

    const options = { passive: true, capture: true };
    this.zone.runOutsideAngular(() => scroller.addEventListener('scroll', onScroll, options));
    this.destroyRef.onDestroy(() => scroller.removeEventListener('scroll', onScroll, options));
  }

  private setHidden(value: boolean): void {
    if (this.hidden() === value) return;
    this.scroller?.classList.toggle('nav-hidden', value);
    this.zone.run(() => this.hidden.set(value));
  }

  /** Focus or a menu opening brings the bar back so keyboard and screen-reader users never lose it. */
  reveal(): void {
    this.scroller?.classList.remove('nav-hidden');
    this.hidden.set(false);
  }
}
